// Detecta dominios de correo mal tipeados para poder sugerirle la corrección
// al lead mientras llena el formulario.
//
// Por qué existe: el 02-08-2026 un lead se registró como
// "fcopuebla8@gmail.con" y después reservó su llamada con el email bueno.
// El bot de agenda no pudo cruzar la cita con su fila, así que no recibió la
// confirmación ni los recordatorios, y encima le siguió llegando la cadena de
// rescate con la llamada ya reservada. Un dominio que no existe además lo deja
// fuera de todas las campañas de email para siempre, sin que nadie se entere:
// los rebotes no se miran uno por uno.
//
// OJO: hay un gemelo de este archivo en backend/src/services/emailDominios.js,
// que es el que tolera el typo al cruzar las citas con las filas. Los dos
// tienen que mantener la MISMA lista de dominios: si acá se agrega uno,
// agregarlo allá también. Están duplicados y no compartidos porque el
// frontend (CRA) y el backend son dos proyectos separados, sin un paquete
// común entre medio.

/**
 * Dominios de correo masivos, más sus variantes regionales REALES.
 *
 * Cumplen dos papeles a la vez: son los destinos hacia los que se corrige, y
 * son intocables (si el dominio está acá, jamás se sugiere cambiarlo). Lo
 * segundo importa tanto como lo primero: hotmail.cl, outlook.cl y live.cl
 * existen de verdad, y sugerirle a alguien que "corrija" el suyo a .com sería
 * mandarlo a escribir mal un correo que tenía bien.
 *
 * mail.com, email.com y ymail.com están en la lista por ese segundo motivo,
 * no por el primero: los tres son proveedores reales y quedan a UNA letra de
 * gmail.com. Sin ellos acá, a alguien con correo en mail.com se le sugeriría
 * "corregirlo" a gmail.com — el error exacto que esto viene a evitar, pero al
 * revés. Salió a la luz escaneando la planilla: había una fila con
 * "testttt@mail.com".
 *
 * La lista se mantiene corta a propósito. Cada dominio agregado es un blanco
 * más al que se puede acercar un dominio legítimo por accidente.
 */
export const DOMINIOS_CONOCIDOS = [
  'gmail.com', 'googlemail.com',
  'mail.com', 'email.com', 'ymail.com',
  'hotmail.com', 'hotmail.cl', 'hotmail.es',
  'outlook.com', 'outlook.cl', 'outlook.es',
  'live.com', 'live.cl',
  'msn.com',
  'yahoo.com', 'yahoo.cl', 'yahoo.es',
  'icloud.com', 'me.com',
  'protonmail.com', 'proton.me',
  'aol.com',
];

const CONOCIDOS = new Set(DOMINIOS_CONOCIDOS);

/**
 * Distancia de Damerau-Levenshtein: ediciones mínimas entre dos strings,
 * contando el intercambio de dos letras contiguas como UNA sola.
 *
 * La transposición es justamente lo que distingue esto de un Levenshtein
 * pelado: "gmial.com" es el typo más común de "gmail.com" y para Levenshtein
 * clásico está a distancia 2, o sea que se escaparía del umbral.
 */
const distanciaDamerau = (a, b) => {
  const m = a.length;
  const n = b.length;
  const d = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 0; i <= m; i += 1) d[i][0] = i;
  for (let j = 0; j <= n; j += 1) d[0][j] = j;

  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const costo = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i][j] = Math.min(
        d[i - 1][j] + 1, // borrado
        d[i][j - 1] + 1, // inserción
        d[i - 1][j - 1] + costo, // sustitución
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1); // transposición
      }
    }
  }

  return d[m][n];
};

/** Parte local y dominio, en minúsculas, o null si no es un email parseable. */
const partirEmail = (raw) => {
  const texto = String(raw ?? '').trim().toLowerCase();
  const partes = texto.split('@');
  if (partes.length !== 2) return null;
  const [local, dominio] = partes;
  if (!local || !dominio) return null;
  return { local, dominio };
};

/**
 * ¿Este email tiene un dominio que parece un typo de uno conocido?
 *
 * Solo sugiere a UNA edición de distancia. Dos errores ya no son un resbalón
 * de tipeo, y a esa distancia la "corrección" sería adivinar: sugerirle a
 * alguien un correo que no es el suyo es peor que no decir nada.
 *
 * Si el dominio queda a una edición de DOS conocidos distintos, tampoco se
 * sugiere: sin forma de saber cuál quiso escribir, la respuesta honesta es no
 * elegir.
 *
 * @returns {string|null} el email corregido, o null si no hay nada que sugerir.
 */
export const corregirDominioTipeado = (raw) => {
  const partes = partirEmail(raw);
  if (!partes) return null;

  const { local, dominio } = partes;
  if (CONOCIDOS.has(dominio)) return null;

  const candidatos = DOMINIOS_CONOCIDOS.filter((d) => distanciaDamerau(dominio, d) === 1);
  if (candidatos.length !== 1) return null;

  return `${local}@${candidatos[0]}`;
};
